import { ChatbotChart } from '@/common/ai/ChatbotChart';
import { FloatingPanel } from '@/common/dialog/FloatingPanel';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, CircularProgress, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useRsrc } from '../rsrc/RsrcContext';
import { useChatbotMgr } from './ChatbotMgrContext';

// Mirrors the limit enforced by ChatDataPlugin
const MAX_INPUT_CHARS = 4000;

/**
 * In-app chatbot panel, toggled from the Help button in the side rail (see ChatbotMgrContext).
 *
 * messages are what the user sees; exchanges are what the backend returned (input, tool calls,
 * response) and are sent back whole on each request so the model keeps its context (see ChatExchange).
 */
export const Chatbot = () => {
  const { open, setOpen, uiComponents } = useChatbotMgr();
  const rsrc = useRsrc();

  const [messages, setMessages] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const tooLong = inputText.length > MAX_INPUT_CHARS;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  const handleAsk = () => {
    const text = inputText.trim();
    if (!text || loading || tooLong) return;

    setInputText('');
    setMessages((prev) => [...prev, { role: 'user', text }]);
    setLoading(true);
    rsrc
      .data('admin', 'CHAT_DATA_PLUGIN', {
        inputText: text,
        history: exchanges,
        // Captured now so the backend answers about what the user saw when asking
        uiComponents: uiComponents(),
      })
      .then(({ exchange, chart }) => {
        setExchanges((prev) => [...prev, exchange]);
        setMessages((prev) => [...prev, { role: 'assistant', text: exchange.responseText, chart }]);
      })
      .finally(() => setLoading(false));
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleAsk();
    }
  };

  return (
    <FloatingPanel open={open} storageKey="chatbot-panel-box">
      <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
        <FontAwesomeIcon icon={fas.faCircleQuestion} style={{ marginRight: 8, opacity: 0.8 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
          Ask Kernel
        </Typography>
        <Tooltip title="Close">
          <IconButton size="small" onClick={() => setOpen(false)}>
            <FontAwesomeIcon icon={fas.faXmark} />
          </IconButton>
        </Tooltip>
      </Stack>

      <Box ref={scrollRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1.5 }}>
        {messages.length === 0 && !loading && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4, px: 2 }}>
            Ask about the data on this page or anywhere in Kernel
          </Typography>
        )}
        <Stack spacing={1.25}>
          {messages.map((message, index) => (
            <Message key={index} message={message} />
          ))}
          {loading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.5, py: 1, color: 'text.secondary' }}>
              <CircularProgress size={14} thickness={5} />
              <Typography variant="body2">Thinking…</Typography>
            </Stack>
          )}
        </Stack>
      </Box>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}>
        <TextField
          value={inputText}
          onChange={(event) => setInputText(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask a question…"
          size="small"
          multiline
          maxRows={4}
          error={tooLong}
          helperText={tooLong ? `At most ${MAX_INPUT_CHARS.toLocaleString()} characters` : null}
        />
        <Button
          variant="contained"
          onClick={handleAsk}
          disabled={loading || !inputText.trim() || tooLong}
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : <FontAwesomeIcon icon={fas.faPaperPlane} />
          }
        >
          Ask
        </Button>
      </Stack>
    </FloatingPanel>
  );
};

const Message = ({ message }) => {
  // Markdown styling for assistant bubbles. List markers are drawn with ::before because the native
  // markers do not paint reliably inside these bubbles
  const markdownSx = (theme) => ({
    '& p': { m: 0 },
    '& p + p': { mt: 0.75 },
    '& ul, & ol': { m: 0, mt: 0.75, mb: 0.75, pl: 0, listStyle: 'none' },
    '& ol': { counterReset: 'kr-ol' },
    '& li': { position: 'relative', pl: '1.4em', mb: 0.6 },
    '& li:last-child': { mb: 0 },
    '& li > p': { m: 0, display: 'inline' },
    '& ul > li::before': { content: '"\\2022"', position: 'absolute', left: '0.4em' },
    '& ol > li': { counterIncrement: 'kr-ol' },
    '& ol > li::before': { content: 'counter(kr-ol) "."', position: 'absolute', left: 0 },
    '& table': { borderCollapse: 'collapse', mt: 0.75, mb: 0.75 },
    '& th, & td': { border: `1px solid ${theme.palette.divider}`, px: 0.75, py: 0.25, textAlign: 'left' },
    '& code': { fontFamily: 'monospace', bgcolor: theme.palette.surface.container, px: 0.5, borderRadius: 1 },
  });

  const isUser = message.role === 'user';
  return (
    <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
      <Box sx={{ maxWidth: '85%' }}>
        <Typography
          component="div"
          variant="body2"
          sx={(theme) => ({
            px: 2,
            py: 1.5,
            borderRadius: 1.5,
            whiteSpace: isUser ? 'pre-wrap' : 'normal',
            bgcolor: isUser ? theme.palette.primary.main : theme.palette.surface.low,
            color: isUser ? theme.palette.primary.contrastText : theme.palette.text.primary,
            ...(isUser ? {} : markdownSx(theme)),
          })}
        >
          {isUser ? message.text : <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.text}</ReactMarkdown>}
        </Typography>
        {message.chart && <ChatbotChart chart={message.chart} />}
      </Box>
    </Box>
  );
};
